import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/lib/context/auth-context";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, ChevronLeft } from "lucide-react";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { Separator } from "@/components/ui/separator";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = (location.state as any)?.from?.pathname || "/";

  useEffect(() => {
    if (user) {
      console.log("User authenticated, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await signIn(email, password);
      // Navigation will be handled by the useEffect above
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error instanceof Error ? error.message : "An error occurred during login";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-gradient-to-br from-[#a7c4d2] via-[#ccd9df] to-[#e8eef1] relative overflow-hidden py-8">
      {/* Abstract background shapes */}
      <div className="absolute inset-0 w-full h-full">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#8ab3c5]/20 blur-[100px] animate-blob"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-[#b9cdd6]/20 blur-[100px] animate-blob animation-delay-2000"></div>
        <div className="absolute top-[40%] left-[30%] w-[50%] h-[50%] rounded-full bg-[#d5e0e5]/20 blur-[100px] animate-blob animation-delay-4000"></div>
        </div>
        
      <div className="w-full max-w-md px-4 relative z-10">
        <Card className="backdrop-blur-sm bg-white/95 shadow-sm border-0 rounded-3xl overflow-hidden">
          <CardHeader className="space-y-1 pb-4 relative">
            <div className="flex flex-col items-center mb-2">
              <img 
                src="/lovable-uploads/cc742b1a-3eb0-4c19-b31f-8e524aa87815.png"
                alt="NWSISD Logo"
                className="h-20 w-auto mb-4"
              />
            </div>
            <CardTitle className="text-2xl text-center font-title text-[#004161] font-medium">Welcome Back</CardTitle>
            <CardDescription className="text-center font-sans text-[#004161]/70">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pb-6 relative">
            <div className="bg-[#0f8f8c]/10 border border-[#0f8f8c]/20 rounded-xl p-4 text-center">
              <p className="text-sm text-[#004161] font-medium mb-2">Looking for upcoming events?</p>
              <Link 
                to="/events" 
                className="inline-flex items-center gap-2 text-[#0f8f8c] hover:text-[#0f8f8c]/80 font-medium transition-all duration-300"
              >
                View All Events 
                <ChevronLeft className="h-4 w-4 rotate-180" />
              </Link>
            </div>
            {error && (
              <Alert variant="destructive" className="bg-destructive/10 border border-destructive/20 text-destructive">
                <AlertDescription className="font-medium">{error}</AlertDescription>
              </Alert>
            )}
            
            <GoogleSignInButton />
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full bg-[#004161]/10" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-[#004161]/60 font-serif">
                  or continue with email
                </span>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[#004161] font-serif">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="bg-transparent border-[#004161]/10 focus:border-[#004161]/20 focus:ring-0 transition-all duration-300 placeholder:text-[#004161]/40 rounded-xl font-serif"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[#004161] font-serif">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="bg-transparent border-[#004161]/10 focus:border-[#004161]/20 focus:ring-0 transition-all duration-300 placeholder:text-[#004161]/40 rounded-xl font-serif"
                />
                <div className="text-right">
                  <Link 
                    to="/auth/forgot-password" 
                    className="text-sm text-[#004161]/70 hover:text-[#004161] transition-all duration-300 font-serif"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#004161] hover:bg-[#004161]/90 text-white transition-all duration-300 font-serif py-5 rounded-xl mt-2" 
                disabled={isLoading || !email || !password}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4 pt-2 pb-6 relative">
            <p className="text-sm text-center text-[#004161]/70 font-serif">
              Don't have an account?{" "}
              <Link to="/auth/register" className="text-[#0f8f8c] hover:text-[#0f8f8c]/80 transition-all duration-300">
                Sign up
              </Link>
            </p>
            
            <div className="flex justify-center gap-6 text-xs text-[#004161]/50 font-serif">
              <Link to="/privacy-policy" className="hover:text-[#0f8f8c] transition-all duration-300">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="hover:text-[#0f8f8c] transition-all duration-300">
                Terms of Service
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
